import React, { useCallback, useEffect, useState } from "react";
import DataSetupMainCard from "./DataSetupMainCard";
import DataSetupDrawer from "./DataSetupDrawer";
import { MuiTable, TextValidator } from "components";
import {
  IconButton,
  Tooltip,
  Box,
  Chip,
  Dialog,
  Typography,
  CircularProgress,
  Switch,
  Grow,
  FormControlLabel,
  Checkbox,
  useTheme,
  Stack,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import StatusConfirmDialog from "components/StatusConfirmDialog";
import ModifierService from "services/ModifierService";

const ModifiersSetupCard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    search: null,
  });

  const [drawer, setDrawer] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isRequired: false,
    minSelections: "",
    maxSelections: "",
    displayOrder: "",
  });

  // Status update state
  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    modifierId: null,
    modifierName: "",
    newStatus: null,
  });

  // View modal state
  const [viewModal, setViewModal] = useState({
    open: false,
    item: null,
  });

  const setPage = (page) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: page,
    }));
  };

  const setPageSize = (pageSize) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      page: 1,
      limit: pageSize,
    }));
  };

  const getData = useCallback(async () => {
    setLoading(true);
    const response = await ModifierService.getAll({
      ...filters,
      page: filters.page - 1,
    });
    if (response.success) {
      setData(response?.data?.view?.data || []);
      setTotalItems(response?.data?.view?.totalItems || 0);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleAddClick = () => {
    setIsEdit(false);
    setEditingId(null);
    setFormData({
      name: "",
      description: "",
      isRequired: false,
      minSelections: "",
      maxSelections: "",
      displayOrder: "",
    });
    setDrawer(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleViewClick = (item) => {
    setViewModal({
      open: true,
      item: item,
    });
  };

  const handleEditClick = (item) => {
    setIsEdit(true);
    setEditingId(item.id);
    setFormData({
      name: item.name || "",
      description: item.description || "",
      isRequired: item.isRequired || false,
      minSelections: item.minSelections || "",
      maxSelections: item.maxSelections || "",
      displayOrder: item.displayOrder || "",
    });
    setDrawer(true);
  };

  const handleStatusToggle = (item) => {
    setStatusDialog({
      open: true,
      loading: false,
      modifierId: item.id,
      modifierName: item.name,
      newStatus: !item.isActive,
    });
  };

  const handleStatusConfirm = async () => {
    const { modifierId, newStatus } = statusDialog;

    try {
      setStatusDialog((prev) => ({ ...prev, loading: true }));

      const payload = {
        isActive: newStatus,
        companyId: filters.companyId,
      };

      const response = await ModifierService.updateStatus(modifierId, payload);

      if (response.success) {
        await getData();
        console.log("✅ Modifier status updated successfully");
      } else {
        console.error("❌ Failed to update modifier status:", response.error);
      }

      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    } catch (error) {
      console.error("Error updating status:", error);
      setStatusDialog((prev) => ({ ...prev, open: false, loading: false }));
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        isRequired: formData.isRequired,
        minSelections: parseInt(formData.minSelections) || 0,
        maxSelections: parseInt(formData.maxSelections) || 0,
        displayOrder: parseInt(formData.displayOrder) || 0,
        companyId: filters.companyId,
      };

      if (isEdit) {
        await ModifierService.update(editingId, payload);
      } else {
        await ModifierService.create(payload);
      }

      setFormData({
        name: "",
        description: "",
        isRequired: false,
        minSelections: "",
        maxSelections: "",
        displayOrder: "",
      });
      setEditingId(null);
      setDrawer(false);
      getData();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  return (
    <DataSetupMainCard
      iconSrc="/assets/images/icons/modifiers.png"
      title="Addons"
      subtitle="Manage your modifiers"
      actionIconSrc="/assets/images/icons/plus.png"
      actionText="Add Addons"
      onAddClick={handleAddClick}
    >
      <MuiTable
        columns={[
          {
            accessorKey: "actions",
            header: "Actions",
            Cell: ({ cell }) => {
              const item = cell.row.original;
              const isUpdating =
                statusDialog.modifierId === item.id && statusDialog.loading;

              return (
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <Tooltip title="View">
                    <IconButton
                      size="small"
                      color="info"
                      onClick={() => handleViewClick(item)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(item)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={item.isActive ? "Deactivate" : "Activate"}>
                    <Box sx={{ position: "relative" }}>
                      <Switch
                        checked={item.isActive}
                        size="small"
                        disabled={isUpdating}
                        onChange={() => handleStatusToggle(item)}
                      />
                      {isUpdating && (
                        <CircularProgress
                          size={20}
                          sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            opacity: 0.7,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            },
          },
          {
            accessorKey: "name",
            header: "Name",
          },
          {
            accessorKey: "description",
            header: "Description",
          },
          {
            accessorKey: "isRequired",
            header: "Required",
            Cell: ({ cell }) => (
              <Chip
                label={cell.getValue() ? "Yes" : "No"}
                size="small"
                color={cell.getValue() ? "success" : "default"}
                variant="outlined"
              />
            ),
          },
          {
            accessorKey: "minSelections",
            header: "Min Selections",
          },
          {
            accessorKey: "maxSelections",
            header: "Max Selections",
          },
          {
            accessorKey: "displayOrder",
            header: "Display Order",
          },
          {
            accessorKey: "isActive",
            header: "Status",
            Cell: ({ cell }) => (
              <Chip
                label={cell.getValue() ? "Active" : "Inactive"}
                size="small"
                color={cell.getValue() ? "success" : "error"}
              />
            ),
          },
        ]}
        data={data}
        title=""
        manualPagination={true}
        rowCount={totalItems}
        currentPage={filters.page}
        pageSize={filters.limit}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        loading={loading}
      />

      <DataSetupDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        iconSrc="/assets/images/icons/modifiers.png"
        title={isEdit ? "Edit Modifier" : "Add Modifier"}
        subtitle="Create and manage modifier details"
        submitLabel={isEdit ? "Update" : "Create"}
        onSubmit={handleFormSubmit}
      >
        <Box gap={2} display="flex" flexDirection="column">
          {/* Modifier Name */}
          <TextValidator
            label="Modifier Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            validators={["required", "minStringLength:2"]}
            errorMessages={[
              "Modifier name is required",
              "Modifier name must be at least 2 characters",
            ]}
            fullWidth
            size="small"
            placeholder="Enter modifier name (e.g., Spice Level)"
          />

          {/* Description */}
          <TextValidator
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            validators={["required"]}
            errorMessages={["Description is required"]}
            fullWidth
            size="small"
            multiline
            rows={3}
            placeholder="Enter modifier description"
          />

          {/* Is Required */}
          <FormControlLabel
            control={
              <Checkbox
                name="isRequired"
                checked={formData.isRequired}
                onChange={handleInputChange}
              />
            }
            label="Is Required"
          />

          {/* Min Selections */}
          <TextValidator
            label="Minimum Selections"
            name="minSelections"
            value={formData.minSelections}
            onChange={handleInputChange}
            validators={["isNumber"]}
            errorMessages={["Minimum selections must be a number"]}
            fullWidth
            size="small"
            type="number"
            placeholder="Enter minimum selections"
            inputProps={{ min: "0" }}
          />

          {/* Max Selections */}
          <TextValidator
            label="Maximum Selections"
            name="maxSelections"
            value={formData.maxSelections}
            onChange={handleInputChange}
            validators={["isNumber"]}
            errorMessages={["Maximum selections must be a number"]}
            fullWidth
            size="small"
            type="number"
            placeholder="Enter maximum selections"
            inputProps={{ min: "0" }}
          />

          {/* Display Order */}
          <TextValidator
            label="Display Order"
            name="displayOrder"
            value={formData.displayOrder}
            onChange={handleInputChange}
            validators={["required", "isNumber"]}
            errorMessages={[
              "Display order is required",
              "Display order must be a number",
            ]}
            fullWidth
            size="small"
            type="number"
            placeholder="Enter display order"
            inputProps={{ min: "0" }}
          />
        </Box>
        <></>
      </DataSetupDrawer>

      {/* Status Confirmation Dialog */}
      <StatusConfirmDialog
        open={statusDialog.open}
        onClose={() => setStatusDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={handleStatusConfirm}
        title={
          statusDialog.newStatus ? "Activate Modifier" : "Deactivate Modifier"
        }
        message={
          statusDialog.newStatus
            ? "Are you sure you want to activate this modifier? It will be visible to customers."
            : "Are you sure you want to deactivate this modifier? It will no longer be visible to customers."
        }
        itemName={statusDialog.modifierName}
        newStatus={statusDialog.newStatus}
        loading={statusDialog.loading}
      />

      {/* View Modifier Modal */}
      <Dialog
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={{
          enter: 400,
          exit: 300,
        }}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            maxHeight: "90vh",
            boxShadow: `0 20px 60px rgba(0, 0, 0, 0.15)`,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            transition: "all 0.4s ease-in-out",
          },
        }}
      >
        {/* ===== Header with Icon ===== */}
        <Box
          sx={{
            p: 3,
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Icon Container */}
          <Box
            component="img"
            src="/assets/images/icons/modifiers.png"
            alt="Modifier Icon"
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              objectFit: "contain",
              filter:
                "brightness(0) saturate(100%) invert(43%) sepia(99%) saturate(1235%) hue-rotate(244deg)",
              opacity: 0.8,
            }}
          />

          {/* Title and Subtitle */}
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5,
                letterSpacing: 0.2,
              }}
            >
              Modifier Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              View modifier information
            </Typography>
          </Box>

          {/* Close Button */}
          <IconButton
            onClick={() => setViewModal({ open: false, item: null })}
            sx={{
              mt: -0.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: `${theme.palette.primary.main}20`,
                color: theme.palette.primary.main,
              },
              transition: "all 0.2s ease",
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ===== Content ===== */}
        <Box
          sx={{
            p: 3,
            flexGrow: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: theme.palette.background.default,
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: theme.palette.divider,
              borderRadius: 3,
              "&:hover": {
                backgroundColor: theme.palette.text.disabled,
              },
            },
          }}
        >
          {viewModal.item && (
            <Stack spacing={3}>
              {/* Modifier Name */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Name
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.name}
                </Typography>
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Description
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.description}
                </Typography>
              </Box>

              {/* Is Required */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Is Required
                </Typography>
                <Chip
                  label={viewModal.item.isRequired ? "Yes" : "No"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: viewModal.item.isRequired
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.info.main}15 0%, ${theme.palette.info.light}15 100%)`,
                    color: viewModal.item.isRequired
                      ? theme.palette.success.dark
                      : theme.palette.info.dark,
                    border: `2px solid ${
                      viewModal.item.isRequired
                        ? theme.palette.success.main
                        : theme.palette.info.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: viewModal.item.isRequired
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.info.main}40`,
                    },
                  }}
                />
              </Box>

              {/* Minimum Selections */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Minimum Selections
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.minSelections}
                </Typography>
              </Box>

              {/* Maximum Selections */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Maximum Selections
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.maxSelections}
                </Typography>
              </Box>

              {/* Display Order */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Display Order
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {viewModal.item.displayOrder}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    display: "block",
                    mb: 0.75,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  Status
                </Typography>
                <Chip
                  label={viewModal.item.isActive ? "Active" : "Inactive"}
                  variant="filled"
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: 0.3,
                    background: viewModal.item.isActive
                      ? `linear-gradient(135deg, ${theme.palette.success.main}15 0%, ${theme.palette.success.light}15 100%)`
                      : `linear-gradient(135deg, ${theme.palette.warning.main}15 0%, ${theme.palette.warning.light}15 100%)`,
                    color: viewModal.item.isActive
                      ? theme.palette.success.dark
                      : theme.palette.warning.dark,
                    border: `2px solid ${
                      viewModal.item.isActive
                        ? theme.palette.success.main
                        : theme.palette.warning.main
                    }`,
                    borderRadius: "20px",
                    padding: "4px 8px",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-2px)",
                      boxShadow: viewModal.item.isActive
                        ? `0 4px 12px ${theme.palette.success.main}40`
                        : `0 4px 12px ${theme.palette.warning.main}40`,
                    },
                  }}
                />
              </Box>
            </Stack>
          )}
        </Box>
      </Dialog>
    </DataSetupMainCard>
  );
};

export default ModifiersSetupCard;
